import { cn } from './cn'

export interface DonutSlice {
  label: string
  value: number
  /** Tailwind stroke token class, e.g. "stroke-ok". Falls back to the series palette. */
  toneClass?: string
}

export interface DonutProps {
  data?: DonutSlice[]
  ariaLabel?: string
  empty?: boolean
  emptyLabel?: string
  loading?: boolean
  className?: string
}

// Canonical VJAL data-viz series order (teal is DATA-VIZ ONLY).
const seriesPalette = ['stroke-chart-1', 'stroke-chart-2', 'stroke-chart-3', 'stroke-chart-4', 'stroke-chart-5', 'stroke-chart-6']

/**
 * House-built inline-SVG donut for a category breakdown (e.g. report-status).
 * Honest empty card when every slice is zero / `data === []`.
 */
export function Donut({
  data = [],
  ariaLabel,
  empty,
  emptyLabel = 'No reports yet',
  loading = false,
  className = ''
}: DonutProps) {
  const total = data.reduce((sum, slice) => sum + slice.value, 0)
  const isEmpty = empty ?? (data.length === 0 || total === 0)

  if (loading) {
    return <EmptyDonut className={className} label="Loading…" role="status" />
  }
  if (isEmpty) {
    return <EmptyDonut className={className} label={emptyLabel} />
  }

  const radius = 42
  const circumference = 2 * Math.PI * radius
  let offset = 0
  const summary =
    ariaLabel ?? `Breakdown of ${total} across ${data.length} categories: ${data.map((s) => `${s.label} ${s.value}`).join(', ')}.`

  return (
    <figure className={cn('m-0 flex flex-wrap items-center gap-4', className)}>
      <svg viewBox="0 0 100 100" className="h-28 w-28 -rotate-90" role="img" aria-label={summary}>
        <circle cx="50" cy="50" r={radius} fill="none" className="stroke-stone" strokeWidth="12" />
        {data.map((slice, index) => {
          const fraction = slice.value / total
          const dash = fraction * circumference
          const segment = (
            <circle
              key={slice.label}
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              className={slice.toneClass ?? seriesPalette[index % seriesPalette.length]}
              strokeWidth="12"
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
            >
              <title>{`${slice.label}: ${slice.value}`}</title>
            </circle>
          )
          offset += dash
          return segment
        })}
      </svg>
      <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
        {data.map((slice, index) => (
          <li key={`legend:${slice.label}`} className="flex items-center gap-2 text-ink-body">
            {/* Swatch is an inline SVG that REUSES the arc's stroke-* token. A
                runtime `.replace('stroke-','bg-')` produced a bg- class Tailwind's
                static scanner never sees, so most swatches rendered blank; the
                stroke- token is guaranteed in the CSS because the arc above uses it. */}
            <svg viewBox="0 0 12 12" className="h-2.5 w-2.5 shrink-0" aria-hidden="true">
              <circle
                cx="6"
                cy="6"
                r="3"
                fill="none"
                strokeWidth="6"
                className={slice.toneClass ?? seriesPalette[index % seriesPalette.length]}
              />
            </svg>
            <span className="tabular-nums font-medium text-strong">{slice.value}</span>
            <span className="text-ink-muted">{slice.label}</span>
          </li>
        ))}
      </ul>
      <figcaption className="sr-only">{summary}</figcaption>
    </figure>
  )
}

function EmptyDonut({ className = '', label, role }: { className?: string; label: string; role?: string }) {
  return (
    <div
      className={cn('grid h-28 w-full place-items-center rounded-control border border-dashed border-line bg-whisper', className)}
      role={role}
    >
      <span className="px-4 text-center text-xs leading-5 text-ink-muted">{label}</span>
    </div>
  )
}
